import styled from 'styled-components';
import { useUserContext } from 'contexts/UserContext';

import { PromoPanel } from 'components/Toolkit/PromoPanel/PromoPanel';
import { Button } from 'components/Toolkit/Button/Button';
import { ButtonLink } from 'components/Toolkit/Button/ButtonLink';
import { media } from 'helpers/breakpoints';

import type { FinanceInfo } from 'components/PageWrappers/AdDetails/types';
import { pushToDataLayer } from 'services/gtmService';
import { generateFinanceLink } from 'components/PageWrappers/AdDetails/helpers';

export interface PromoPanelCollectionProps {
  breadCrumbSectionName?: string | null;
  sectionName?: string;
  cdnUrl: string;
  showGreenlight?: boolean;
  className?: string;
  displayOnDesktopOnly?: boolean;
  financeInfo: FinanceInfo | null;
}

const Container = styled.div<{ displayOnDesktopOnly?: boolean }>`
  display: ${({ displayOnDesktopOnly }) =>
    displayOnDesktopOnly ? 'none' : 'block'};

  ${media.large} {
    display: ${({ displayOnDesktopOnly }) =>
      displayOnDesktopOnly ? 'block' : 'none'};
  }
`;

function PromoPanelCollection({
  breadCrumbSectionName,
  sectionName,
  cdnUrl,
  showGreenlight,
  className,
  displayOnDesktopOnly,
  financeInfo,
}: PromoPanelCollectionProps) {
  const { setIsHistoryCheckModalOpen } = useUserContext();
  const hasFinanceInfo = Boolean(financeInfo);
  const isHistoryCheck = Boolean(!hasFinanceInfo && showGreenlight);

  if (hasFinanceInfo) {
    className = `${className} ${financeInfo?.partnerName}-${financeInfo?.partnerType}-imp`;
  }

  if (isHistoryCheck) {
    className = `${className} donedeal-historycheck-imp`;
  }

  const handlePanelClick = () => {
    if (hasFinanceInfo) {
      pushToDataLayer({
        event: 'partner_clicks',
        servicePartnerType: financeInfo?.partnerType,
        servicePartnerName: financeInfo?.partnerName,
      });
    }

    if (isHistoryCheck) {
      pushToDataLayer({
        event: 'partner_clicks',
        servicePartnerType: 'historycheck',
        servicePartnerName: 'greenlight',
      });
    }
  };

  return (
    <Container
      className={className}
      displayOnDesktopOnly={displayOnDesktopOnly}
      onClick={handlePanelClick}
    >
      {hasFinanceInfo && (
        <PromoPanel
          title="Finance your vehicle"
          listItems={['Get approved for finance on this car']}
          illustration={`${cdnUrl}/images/illustrations/finance.svg`}
          renderButton={
            <ButtonLink
              href={generateFinanceLink(financeInfo!)}
              data-tracking="get-finance-approval"
              target="_blank"
              rel="noopener nofollow"
            >
              {financeInfo!.buttonText}
            </ButtonLink>
          }
        />
      )}

      {isHistoryCheck && (
        <PromoPanel
          title="Greenlight History Check"
          listItems={['Get Peace of Mind']}
          illustration={`${cdnUrl}/images/illustrations/greenlight.svg`}
          renderButton={
            <Button onClick={() => setIsHistoryCheckModalOpen(true)}>
              Purchase History Check Report
            </Button>
          }
        />
      )}
      {breadCrumbSectionName === 'animals' && (
        <PromoPanel
          title="DoneDeal is an IPAAG Member"
          listItems={['Irish Pet Advertising Advisory Group']}
          illustration={`${cdnUrl}/images/illustrations/IPAAG.svg`}
          renderButton={
            <ButtonLink
              href={
                sectionName === 'reptiles'
                  ? 'http://ipaag.ie/buying-a-pet-advice/exotic-pets/?utm_source=DoneDeal&utm_medium=BANNER&utm_campaign=Reptiles_June_2018&utm_term=MAY_18&utm_content=REPTILES'
                  : 'https://ipaag.ie/?utm_source=DoneDeal&utm_medium=BANNER&utm_campaign=MAY_2018&utm_term=MAY_18&utm_content=IPAAG_Logo_DoneDeal'
              }
              target="_blank"
              rel="noopener"
            >
              Learn More
            </ButtonLink>
          }
        />
      )}
    </Container>
  );
}

export { PromoPanelCollection };
